import React, { useContext, useEffect, useRef, useState } from 'react'
import { Advert, FixedAdvert } from '@chegg-wt/core/components/Advert'
import ResponsiveAdStrategy from '@chegg-wt/core/components/ResponsiveAdStrategy'
import { GetLVPLayoutFn } from '@chegg-wt/core/types/layout'
import { reloadAds } from '@chegg-wt/core/utils/ads'
import {
  LOW_AD_VARIATION1,
  LOW_AD_VARIATION2,
  LOW_AD_VARIATION3,
} from '@chegg-wt/core/state/optimizely/constants'
import usePriceTest from '@chegg-wt/core/hooks/usePriceTest'
import { EB_PRODUCT_NAME } from '@chegg-wt/core/constants/site'
import { useSelector } from 'react-redux'
import { citationValidityVariationSelector } from '@chegg-wt/core/state/bibliography/selectors'
import {
  isWritingAssistantHomeEnabledSelector,
  isParaphraseFlagEnabledSelector,
} from '@chegg-wt/core-wa/state/selectors'
import { Config } from '@chegg-wt/core/config'
import { EntryPointBanner } from '@chegg-wt/core-wa/components/EntryPointBanner/EntryPointBanner'
import UpgradeBanner from '../UpgradeBanner'
import CreateAccountBanner from '../CreateAccountBanner'
import { CitationValidityVariations } from '../../types/core'
import * as Styled from './styled'

// The context for whether lvp R3 is enabled and the ref for the content area
// defined here because we only want to partially expose the context through the helpers below
const LvpContext = React.createContext({
  showLvp: false,
  isLvpR3Active: false,
  isStickyDisabled: false,
  lvpContentRef: null as React.MutableRefObject<any>,
  adConfigVersion: null,
  currentPage: null,
})

export const useLvpContext = () => useContext(LvpContext)

// provider to mount in app to say whether or not lvp R3 is enabled, define lvpContentRef
export const LvpProvider = ({
  children,
  showLvp,
  isLvpR3Active,
  isStickyDisabled,
  adConfigVersion,
  currentPage,
}) => {
  // ref for lvp content area to use with a portal
  const lvpContentRef = useRef(null)
  return (
    <LvpContext.Provider
      value={{
        showLvp,
        adConfigVersion,
        isLvpR3Active,
        isStickyDisabled,
        lvpContentRef,
        currentPage,
      }}
    >
      {children}
    </LvpContext.Provider>
  )
}

// hook to determine if lvp should be shown
export const useLvp = () => {
  const { showLvp } = useContext(LvpContext)
  return showLvp
}

//  a wrapping element that adds margin on the right side for the adbar if lvp is active
export const LvpMarginWrapper = ({ isFullWidth = false, children }) => {
  const { adConfigVersion } = useContext(LvpContext)
  const isLvpActive = useLvp()
  const isHrCenterAlign = adConfigVersion === LOW_AD_VARIATION3
  return isLvpActive ? (
    <Styled.AdMarginWrapper layoutHRCenter={isHrCenterAlign} isFullWidth={isFullWidth}>
      {children}
    </Styled.AdMarginWrapper>
  ) : (
    <>{children}</>
  )
}

// The right hand ad sidebar, large video player area, bottom-anchored ad, and checkbox menu.
const LargeVideoPlayerLayout = ({
  topContent,
  children,
  showCreateAccountBanner,
  showProofReaderBanner,
  isWritingAssistantHome,
}) => {
  const { showLvp, isLvpR3Active, isStickyDisabled, adConfigVersion } = useContext(
    LvpContext
  )
  const isWritingAssistantHomeEnabled = useSelector(isWritingAssistantHomeEnabledSelector)
  const citationValidityVariation = useSelector(citationValidityVariationSelector)
  const [isR3SideviewFixed, setIsR3SideviewFixed] = useState(false)
  const { discountPercentage } = usePriceTest()
  const isProofreaderEnabled = useSelector(isParaphraseFlagEnabledSelector)

  useEffect(() => {
    const scrollHandler = () => {
      if (!isR3SideviewFixed && window.pageYOffset >= 330) {
        return setIsR3SideviewFixed(true)
      }
      if (isR3SideviewFixed && window.pageYOffset < 330) {
        return setIsR3SideviewFixed(false)
      }
    }

    window.addEventListener('scroll', scrollHandler)

    return () => {
      window.removeEventListener('scroll', scrollHandler)
    }
  }, [isR3SideviewFixed])

  // "low ad test EB & CM", Removing lvp ad space, if any of variant is active.
  const hideLvpAdFrame =
    adConfigVersion === LOW_AD_VARIATION1 ||
    adConfigVersion === LOW_AD_VARIATION2 ||
    adConfigVersion === LOW_AD_VARIATION3

  const renderSideView = () => {
    if (isWritingAssistantHome && isWritingAssistantHomeEnabled) {
      return null
    }

    return isLvpR3Active ? (
      <>
        {isStickyDisabled ? (
          <Styled.SideView data-test="side-view">
            <Advert placement="R1" />
            <Advert placement="R2" />
            <Advert placement="R3" />
          </Styled.SideView>
        ) : (
          <Styled.AdvertWrapper>
            <Styled.SideView data-test="side-view">
              <Advert placement="R1" />
            </Styled.SideView>
            <Styled.StickySideView
              isR3SideviewFixed={isR3SideviewFixed}
              topBanner={discountPercentage > 0}
            >
              <Advert placement="R2" />
              <Advert placement="R3" />
            </Styled.StickySideView>
          </Styled.AdvertWrapper>
        )}
      </>
    ) : (
      <Styled.StickySideView data-test="side-view" topBanner={discountPercentage > 0}>
        <Advert placement="R1" />
        <Advert placement="R2" />
      </Styled.StickySideView>
    )
  }

  const getHeaderBanner = () => {
    // For sites other than EB
    if (Config.siteName !== EB_PRODUCT_NAME) return <UpgradeBanner />

    // For EB loading state (No banner)
    if (isProofreaderEnabled === null) return null

    // For EB proofreader
    if (isProofreaderEnabled && showProofReaderBanner) {
      return <EntryPointBanner />
    }
    return <UpgradeBanner />
  }
  if (isWritingAssistantHome) {
    if (isWritingAssistantHomeEnabled === null) return null
  }

  // if lvp is not shown (e.g. the user is paid, or on an auth page), just render top content / children
  if (!showLvp) {
    if (isWritingAssistantHome && isWritingAssistantHomeEnabled) return children
    return (
      <>
        <Styled.Background>
          <Styled.Contents>
            {getHeaderBanner()}
            {topContent()}
          </Styled.Contents>
        </Styled.Background>
        {children}
        <Styled.BottomTray showLvp={showLvp}>
          <Styled.CheckboxMenu id="checkbox-menu" />
        </Styled.BottomTray>
      </>
    )
  }
  const isHrCenterAlign = adConfigVersion === LOW_AD_VARIATION3

  // sideView is before the video area because they are both using fixed position
  // and the video is supposed to be on top. This fixes the stacking order.
  // More info: https://philipwalton.com/articles/what-no-one-told-you-about-z-index/
  return (
    <>
      <ResponsiveAdStrategy renderAds={reloadAds} />
      {/* the right ad sidebar where the ads and docked video are placed */}

      {renderSideView()}
      {/* the video player & top content area */}

      {isWritingAssistantHome && isWritingAssistantHomeEnabled ? (
        children
      ) : (
        <>
          <Styled.Background>
            <Styled.AdMarginWrapper layoutHRCenter={isHrCenterAlign}>
              <Styled.Contents role="region" aria-label="Page Header">
                {/* TODO: remove isProduction() after verifying optimizely */}
                {getHeaderBanner()}
                <Styled.T1Advert placement="T1" className="adds top" />
                {topContent()}
                {showCreateAccountBanner &&
                  citationValidityVariation ===
                    CitationValidityVariations.ABOVE_VID_AD && <CreateAccountBanner />}
                {!hideLvpAdFrame && <Styled.VideoAdvert placement="VID" />}
                {showCreateAccountBanner &&
                  citationValidityVariation ===
                    CitationValidityVariations.BELOW_VID_AD && <CreateAccountBanner />}
              </Styled.Contents>
            </Styled.AdMarginWrapper>
          </Styled.Background>

          {/* the actual page content */}
          {children}

          {/* the bottom fixed placement ad and checkbox menu portal for bibliography / folders page */}
          <Styled.BottomTray showLvp={showLvp}>
            <FixedAdvert placement="B1" />
            <Styled.CheckboxMenu id="checkbox-menu" />
          </Styled.BottomTray>
        </>
      )}
    </>
  )
}

export const getLvpLayout: GetLVPLayoutFn = (
  page,
  topContent = () => null,
  showCreateAccountBanner = false,
  showProofReaderBanner = false,
  isWritingAssistantHome = false
) => (
  <LargeVideoPlayerLayout
    topContent={topContent}
    showCreateAccountBanner={showCreateAccountBanner}
    showProofReaderBanner={showProofReaderBanner}
    isWritingAssistantHome={isWritingAssistantHome}
  >
    {page}
  </LargeVideoPlayerLayout>
)

export default LargeVideoPlayerLayout
